import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
} from '@mui/material';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { costManagementService } from 'services/costManagementService';
import { toast } from 'react-toastify';
import { NewButton } from 'components/NewButton';
import { IVehicleRotation, ITransshipPoint } from 'interfaces/IWaybill';
import { newMoney, newMoneyWithNegative } from 'utils/newMoney';
import { VehicleAutocomplete } from 'components/VehicleAutocomplete';
import dayjs, { Dayjs } from 'dayjs';
import { FormDatePicker } from 'components/FormDatePicker';

interface AddFirstMileFormProps {
  onSuccess: () => void;
  transshipPoints: ITransshipPoint[];
}

interface IFormData {
  vehicleId: number | null;
  vehicle: IVehicleRotation | null;
  transshipPointId: string;
  vehicleCost: string;
  costAdjustment: string;
  leaderCommission: string;
  deliveryDate: Dayjs | null;
}

const validationSchema = Yup.object({
  transshipPointId: Yup.string().required('CD é obrigatório'),
  vehicleId: Yup.number().nullable().required('Veículo é obrigatório'),
  vehicleCost: Yup.string()
    .required('Valor da Rota é obrigatório')
    .test('is-greater-than-zero', 'Valor deve ser maior que zero', (value) => {
      if (!value) return false;
      const numValue = Number(newMoney.clean(value));
      return numValue > 0;
    }),
  leaderCommission: Yup.string()
    .required('Comissão do Líder é obrigatória')
    .test('is-greater-than-zero', 'Valor deve ser maior que zero', (value) => {
      if (!value) return false;
      const numValue = Number(newMoney.clean(value));
      return numValue > 0;
    }),
  costAdjustment: Yup.string(),
  deliveryDate: Yup.mixed<Dayjs | null>().required('Data de entrega é obrigatória').nullable(),
});

export function AddFirstMileForm({ onSuccess, transshipPoints }: AddFirstMileFormProps) {
  const initialValues: IFormData = {
    vehicleId: null,
    vehicle: null,
    transshipPointId: '',
    vehicleCost: '',
    costAdjustment: '0',
    leaderCommission: '',
    deliveryDate: dayjs(),
  };

  const handleSubmit = async (values: IFormData) => {
    if (!values.deliveryDate) {
      toast.error('Data de entrega é obrigatória');
      return;
    }

    try {
      await costManagementService.createFirstMileCost({
        transshipPointId: Number(values.transshipPointId),
        vehicleId: Number(values.vehicleId),
        vehicleCost: Number(values.vehicleCost),
        costAdjustment: Number(values.costAdjustment),
        leaderCommission: Number(values.leaderCommission),
        deliveryDate: values.deliveryDate.format('YYYY-MM-DD'),
      });
      toast.success('First Mile adicionado com sucesso!');
      onSuccess();
    } catch (error: any) {
      const errorMessage = error?.error;
      if (errorMessage) {
        toast.error(`${errorMessage}`);
      } else {
        toast.error('Erro ao adicionar custos de First Mile');
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik: FormikProps<IFormData>) => (
        <Box
          component={Form}
          sx={{ p: { xs: 1, sm: 3 }, display: 'flex', justifyContent: 'center' }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 0, sm: 3 },
              width: '100%',
              maxWidth: '750px',
              boxSizing: 'border-box',
            }}
          >
            <Grid container spacing={3} width="100%">
              <Grid item xs={12}>
                <FormDatePicker
                  label="Data de entrega *"
                  value={formik.values.deliveryDate}
                  onChange={(newValue) => formik.setFieldValue('deliveryDate', newValue)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      error: formik.touched.deliveryDate && Boolean(formik.errors.deliveryDate),
                      helperText: formik.touched.deliveryDate && formik.errors.deliveryDate
                        ? String(formik.errors.deliveryDate)
                        : '',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="medium"
                  error={formik.touched.transshipPointId && Boolean(formik.errors.transshipPointId)}
                >
                  <InputLabel id="transshipPoint-label">CD *</InputLabel>
                  <Select
                    labelId="transshipPoint-label"
                    name="transshipPointId"
                    value={formik.values.transshipPointId}
                    onChange={(e) => formik.setFieldValue('transshipPointId', e.target.value)}
                    onBlur={formik.handleBlur}
                    label="CD *"
                    size="medium"
                    sx={{ height: 56 }}
                  >
                    <MenuItem value="">
                      <em>Selecione um CD</em>
                    </MenuItem>
                    {transshipPoints.map((cd: ITransshipPoint) => (
                      <MenuItem key={cd.id} value={cd.id}>
                        {cd.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.transshipPointId && formik.errors.transshipPointId && (
                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }}>
                      {formik.errors.transshipPointId}
                    </Box>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <VehicleAutocomplete
                  value={formik.values.vehicle}
                  onChange={(vehicle) => {
                    formik.setFieldValue('vehicle', vehicle);
                    formik.setFieldValue('vehicleId', vehicle?.id || null);
                  }}
                  required
                  error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
                  helperText={formik.touched.vehicleId && formik.errors.vehicleId ? String(formik.errors.vehicleId) : ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Valor da Rota *"
                  name="vehicleCost"
                  type="text"
                  value={newMoney.apply(formik.values.vehicleCost)}
                  onChange={(e) => {
                    const cleanValue = newMoney.clean(e.target.value).toString();
                    formik.setFieldValue('vehicleCost', cleanValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vehicleCost && Boolean(formik.errors.vehicleCost)}
                  helperText={formik.touched.vehicleCost && formik.errors.vehicleCost}
                  size="medium"
                  InputProps={{
                    startAdornment: 'R$',
                    sx: { height: 56, fontSize: 16 },
                  }}
                  inputProps={{
                    style: { fontSize: 16 },
                    inputMode: 'numeric',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comissão do Líder *"
                  name="leaderCommission"
                  type="text"
                  value={newMoney.apply(formik.values.leaderCommission)}
                  onChange={(e) => {
                    const cleanValue = newMoney.clean(e.target.value).toString();
                    formik.setFieldValue('leaderCommission', cleanValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.leaderCommission && Boolean(formik.errors.leaderCommission)}
                  helperText={formik.touched.leaderCommission && formik.errors.leaderCommission}
                  size="medium"
                  InputProps={{
                    startAdornment: 'R$',
                    sx: { height: 56, fontSize: 16 },
                  }}
                  inputProps={{
                    style: { fontSize: 16 },
                    inputMode: 'numeric',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ajuste"
                  name="costAdjustment"
                  type="text"
                  value={newMoneyWithNegative.apply(formik.values.costAdjustment)}
                  onChange={(e) => {
                    const cleanValue = newMoneyWithNegative.clean(e.target.value).toString();
                    formik.setFieldValue('costAdjustment', cleanValue);
                  }}
                  size="medium"
                  InputProps={{
                    startAdornment: 'R$',
                    sx: { height: 56, fontSize: 16 },
                  }}
                  inputProps={{
                    style: { fontSize: 16 },
                    inputMode: 'numeric',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    position: { xs: 'fixed', md: 'static' },
                    left: 0,
                    bottom: 0,
                    width: { xs: '100%', md: 'auto' },
                    bgcolor: 'background.paper',
                    boxShadow: { xs: '0 -2px 8px rgba(0,0,0,0.07)', md: 'none' },
                    zIndex: 1201,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  <NewButton
                    variantType="filled"
                    color="primary"
                    type="submit"
                    disabled={formik.isSubmitting}
                    sx={{ minWidth: ['100%', 120] }}
                  >
                    {formik.isSubmitting ? 'Adicionando...' : 'Adicionar'}
                  </NewButton>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </Formik>
  );
}
